import { useState } from "react";
import { motion } from "framer-motion";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Scale,
  CheckCircle,
  FileText,
  TrendingUp,
  MapPin,
  Loader2,
  AlertCircle,
  Search,
  Clock,
} from "lucide-react";
import { Card, Input, Button, Badge, RiskIndicator } from "@bcc/shared-ui";
import type { RiskLevel } from "@bcc/shared-types";
import { AddressAutocomplete } from "../components/AddressAutocomplete";
import {
  assessDevelopmentRisk,
  findSimilarCases,
  type AddressAutocompleteResult,
} from "@bcc/shared-api";
import { useLocation } from "../contexts/LocationContext";
import { SEO } from "../components/SEO";

interface ProjectInput {
  address: string;
  proposedUse: string;
  lotArea: number;
  buildingArea: number;
  height: number;
  frontSetback: number;
  rearSetback: number;
  sideSetback: number;
  parkingSpaces: number;
  zoneCode: string;
  community: string;
  parcelId: string;
}

const initialProject: ProjectInput = {
  address: "",
  proposedUse: "",
  lotArea: 0,
  buildingArea: 0,
  height: 0,
  frontSetback: 0,
  rearSetback: 0,
  sideSetback: 0,
  parkingSpaces: 0,
  zoneCode: "",
  community: "",
  parcelId: "",
};

// Proposed use options organized by category
const proposedUseOptions = [
  // Residential Uses
  {
    value: "single_family",
    label: "Single Family Dwelling",
    category: "Residential",
  },
  {
    value: "single_family_secondary",
    label: "Single Family Dwelling with Secondary Suite",
    category: "Residential",
  },
  {
    value: "duplex",
    label: "Duplex / Semi-Detached Dwelling",
    category: "Residential",
  },
  { value: "rowhouse", label: "Rowhouse / Townhouse", category: "Residential" },
  { value: "fourplex", label: "Fourplex", category: "Residential" },
  {
    value: "apartment_low",
    label: "Low-Rise Apartment (3-4 storeys)",
    category: "Residential",
  },
  {
    value: "apartment_mid",
    label: "Mid-Rise Apartment (5-8 storeys)",
    category: "Residential",
  },
  {
    value: "apartment_high",
    label: "High-Rise Apartment (9+ storeys)",
    category: "Residential",
  },
  {
    value: "laneway_home",
    label: "Laneway / Backyard Suite",
    category: "Residential",
  },
  {
    value: "secondary_suite",
    label: "Secondary Suite (basement)",
    category: "Residential",
  },
  { value: "garage_suite", label: "Garage Suite", category: "Residential" },
  // Commercial Uses
  {
    value: "retail_small",
    label: "Retail Store (small)",
    category: "Commercial",
  },
  {
    value: "retail_large",
    label: "Retail Store (large)",
    category: "Commercial",
  },
  { value: "restaurant", label: "Restaurant / Cafe", category: "Commercial" },
  { value: "office", label: "Office Building", category: "Commercial" },
  {
    value: "medical_clinic",
    label: "Medical / Dental Clinic",
    category: "Commercial",
  },
  {
    value: "daycare",
    label: "Daycare / Child Care Facility",
    category: "Commercial",
  },
  { value: "hotel", label: "Hotel / Motel", category: "Commercial" },
  {
    value: "gas_station",
    label: "Gas Station / Service Station",
    category: "Commercial",
  },
  // Mixed Use
  {
    value: "mixed_residential_commercial",
    label: "Mixed Use (Residential + Commercial)",
    category: "Mixed Use",
  },
  { value: "live_work", label: "Live-Work Unit", category: "Mixed Use" },
  // Industrial
  { value: "warehouse", label: "Warehouse / Storage", category: "Industrial" },
  {
    value: "light_industrial",
    label: "Light Industrial",
    category: "Industrial",
  },
  {
    value: "manufacturing",
    label: "Manufacturing / Production",
    category: "Industrial",
  },
  // Other
  { value: "parking_lot", label: "Parking Lot / Parkade", category: "Other" },
  { value: "place_of_worship", label: "Place of Worship", category: "Other" },
  { value: "community_centre", label: "Community Centre", category: "Other" },
  {
    value: "school",
    label: "School / Educational Facility",
    category: "Other",
  },
];

// Zone-based default use recommendations and typical values
const zoneDefaults: Record<
  string,
  {
    recommendedUse: string;
    typicalLotArea: number;
    typicalBuildingArea: number;
    maxHeight: number;
    frontSetback: number;
    rearSetback: number;
    sideSetback: number;
    parkingSpaces: number;
  }
> = {
  "R-C1": {
    recommendedUse: "single_family",
    typicalLotArea: 450,
    typicalBuildingArea: 180,
    maxHeight: 10,
    frontSetback: 4.0,
    rearSetback: 7.5,
    sideSetback: 1.2,
    parkingSpaces: 2,
  },
  "R-C1N": {
    recommendedUse: "single_family",
    typicalLotArea: 450,
    typicalBuildingArea: 180,
    maxHeight: 10,
    frontSetback: 4.0,
    rearSetback: 7.5,
    sideSetback: 1.2,
    parkingSpaces: 2,
  },
  "R-C2": {
    recommendedUse: "single_family_secondary",
    typicalLotArea: 400,
    typicalBuildingArea: 200,
    maxHeight: 10,
    frontSetback: 3.0,
    rearSetback: 7.5,
    sideSetback: 1.2,
    parkingSpaces: 2,
  },
  "R-CG": {
    recommendedUse: "rowhouse",
    typicalLotArea: 350,
    typicalBuildingArea: 220,
    maxHeight: 11,
    frontSetback: 3.0,
    rearSetback: 6.0,
    sideSetback: 1.2,
    parkingSpaces: 1,
  },
  "R-G": {
    recommendedUse: "apartment_low",
    typicalLotArea: 800,
    typicalBuildingArea: 400,
    maxHeight: 14,
    frontSetback: 3.0,
    rearSetback: 7.5,
    sideSetback: 3.0,
    parkingSpaces: 8,
  },
  "M-C1": {
    recommendedUse: "retail_small",
    typicalLotArea: 500,
    typicalBuildingArea: 300,
    maxHeight: 10,
    frontSetback: 0,
    rearSetback: 6.0,
    sideSetback: 0,
    parkingSpaces: 5,
  },
  "M-C2": {
    recommendedUse: "mixed_residential_commercial",
    typicalLotArea: 600,
    typicalBuildingArea: 500,
    maxHeight: 16,
    frontSetback: 0,
    rearSetback: 6.0,
    sideSetback: 0,
    parkingSpaces: 10,
  },
  "M-CG": {
    recommendedUse: "mixed_residential_commercial",
    typicalLotArea: 500,
    typicalBuildingArea: 400,
    maxHeight: 12,
    frontSetback: 0,
    rearSetback: 6.0,
    sideSetback: 0,
    parkingSpaces: 5,
  },
  "M-H1": {
    recommendedUse: "mixed_residential_commercial",
    typicalLotArea: 1000,
    typicalBuildingArea: 2000,
    maxHeight: 26,
    frontSetback: 0,
    rearSetback: 6.0,
    sideSetback: 3.0,
    parkingSpaces: 20,
  },
  "M-H2": {
    recommendedUse: "apartment_high",
    typicalLotArea: 2000,
    typicalBuildingArea: 5000,
    maxHeight: 46,
    frontSetback: 0,
    rearSetback: 6.0,
    sideSetback: 6.0,
    parkingSpaces: 50,
  },
  "I-G": {
    recommendedUse: "warehouse",
    typicalLotArea: 2000,
    typicalBuildingArea: 1500,
    maxHeight: 14,
    frontSetback: 6.0,
    rearSetback: 3.0,
    sideSetback: 3.0,
    parkingSpaces: 10,
  },
  "I-B": {
    recommendedUse: "light_industrial",
    typicalLotArea: 1500,
    typicalBuildingArea: 1000,
    maxHeight: 12,
    frontSetback: 6.0,
    rearSetback: 3.0,
    sideSetback: 3.0,
    parkingSpaces: 8,
  },
};

// Get suggested uses based on zone
function getSuggestedUsesForZone(zoneCode: string): string[] {
  const zone = zoneCode.toUpperCase();

  // Residential zones
  if (zone.startsWith("R-C1")) {
    return [
      "single_family",
      "single_family_secondary",
      "laneway_home",
      "garage_suite",
    ];
  }
  if (zone.startsWith("R-C2")) {
    return [
      "single_family_secondary",
      "duplex",
      "laneway_home",
      "garage_suite",
      "secondary_suite",
    ];
  }
  if (zone === "R-CG" || zone.startsWith("R-CG")) {
    return [
      "rowhouse",
      "fourplex",
      "single_family_secondary",
      "duplex",
      "apartment_low",
    ];
  }
  if (zone.startsWith("R-G")) {
    return ["apartment_low", "apartment_mid", "rowhouse", "fourplex"];
  }

  // Mixed-use zones
  if (zone.startsWith("M-C") || zone.startsWith("M-CG")) {
    return [
      "mixed_residential_commercial",
      "retail_small",
      "restaurant",
      "office",
      "apartment_low",
    ];
  }
  if (zone.startsWith("M-H")) {
    return [
      "apartment_high",
      "mixed_residential_commercial",
      "office",
      "hotel",
    ];
  }

  // Commercial zones
  if (zone.startsWith("C-")) {
    return [
      "retail_small",
      "retail_large",
      "office",
      "restaurant",
      "medical_clinic",
    ];
  }

  // Industrial zones
  if (zone.startsWith("I-")) {
    return ["warehouse", "light_industrial", "manufacturing", "office"];
  }

  // Default fallback
  return ["single_family", "single_family_secondary", "duplex", "rowhouse"];
}

interface RiskAssessmentResult {
  overall_risk: RiskLevel;
  approval_probability: number;
  factors: Array<{
    category: string;
    description: string;
    impact: RiskLevel;
    details: string;
  }>;
  recommendations: string[];
}

export function SDABExpertPage() {
  const { city, isSupported, locationLabel } = useLocation();
  const [project, setProject] = useState<ProjectInput>(initialProject);
  const [selectedAddress, setSelectedAddress] =
    useState<AddressAutocompleteResult | null>(null);

  // Risk assessment mutation
  const assessmentMutation = useMutation({
    mutationFn: async () => {
      const result = await assessDevelopmentRisk({
        address: project.address,
        proposed_use: project.proposedUse,
        lot_area_sqm: project.lotArea,
        building_area_sqm: project.buildingArea,
        height_m: project.height,
        setbacks: {
          front: project.frontSetback,
          rear: project.rearSetback,
          side: project.sideSetback,
        },
        parking_spaces: project.parkingSpaces,
      });
      return result as RiskAssessmentResult;
    },
  });

  // Similar cases query (runs when we have assessment)
  const similarCasesQuery = useQuery({
    queryKey: ["similarCases", project.proposedUse, project.zoneCode],
    queryFn: () =>
      findSimilarCases({
        property_type: project.proposedUse,
        variance_type: "setback",
        location: project.community || undefined,
      }),
    enabled: !!assessmentMutation.data && !!project.proposedUse,
  });

  const handleAnalyze = () => {
    assessmentMutation.mutate();
  };

  const updateProject = (field: keyof ProjectInput, value: string | number) => {
    setProject((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddressSelect = (result: AddressAutocompleteResult) => {
    setSelectedAddress(result);
    let zoneCode = result.zone_code || "";

    // Normalize zone code format (uppercase, with hyphen)
    if (zoneCode) {
      zoneCode = zoneCode.toUpperCase().trim();
      // Handle formats like "RC1" -> "R-C1", "MC2" -> "M-C2"
      if (!zoneCode.includes("-") && zoneCode.length >= 2) {
        // Insert hyphen after first character for residential/mixed/commercial/industrial zones
        if (/^[RMCIS][A-Z0-9]/.test(zoneCode)) {
          zoneCode = zoneCode.slice(0, 1) + "-" + zoneCode.slice(1);
        }
      }
    }

    // Get zone defaults - try exact match first, then prefix match
    let defaults = zoneDefaults[zoneCode];

    if (!defaults && zoneCode) {
      // Try without suffix (R-C1N -> R-C1, R-C1S -> R-C1)
      const baseZone = zoneCode.replace(/[NS]$/, "");
      defaults = zoneDefaults[baseZone];

      // If still no match, try to find a matching prefix
      if (!defaults) {
        const matchingKey = Object.keys(zoneDefaults).find(
          (key) =>
            zoneCode.startsWith(key) ||
            key.startsWith(zoneCode.split("-").slice(0, 2).join("-")),
        );
        if (matchingKey) {
          defaults = zoneDefaults[matchingKey];
        }
      }
    }

    setProject((prev) => ({
      ...prev,
      address: result.address,
      zoneCode: zoneCode,
      community: result.community || "",
      parcelId: result.parcel_id || "",
      // Prepopulate with zone defaults if available
      ...(defaults && {
        proposedUse: defaults.recommendedUse,
        lotArea: defaults.typicalLotArea,
        buildingArea: defaults.typicalBuildingArea,
        height: defaults.maxHeight,
        frontSetback: defaults.frontSetback,
        rearSetback: defaults.rearSetback,
        sideSetback: defaults.sideSetback,
        parkingSpaces: defaults.parkingSpaces,
      }),
    }));
  };

  // Get suggested uses for current zone
  const suggestedUses = project.zoneCode
    ? getSuggestedUsesForZone(project.zoneCode)
    : [];

  const assessment = assessmentMutation.data;

  return (
    <>
      <SEO />
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Scale className="h-6 w-6 text-teal-500" />
            SDAB Expert System
          </h1>
          <p className="text-gray-500 mt-1">
            Predict development permit outcomes and SDAB appeal success
            probability.
          </p>
        </div>

        {/* Location Not Supported Notice */}
        {!isSupported && (
          <Card variant="outlined" className="p-4 bg-amber-50 border-amber-200">
            <div className="flex gap-3">
              <Clock className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-medium mb-1">
                  {locationLabel} - Coming Soon
                </p>
                <p>
                  SDAB case data for {locationLabel} is not yet available. The
                  analysis below uses Calgary, AB SDAB decisions for reference.
                  We're working on expanding to more Canadian municipalities.
                </p>
              </div>
            </div>
          </Card>
        )}

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Input Form */}
          <Card variant="elevated">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Project Details
            </h2>

            <div className="space-y-4">
              <AddressAutocomplete
                label="Property Address"
                placeholder={`123 Main Street SW, ${isSupported ? city?.name || "Calgary" : "Calgary"}`}
                value={project.address}
                onChange={(value) => updateProject("address", value)}
                onSelect={handleAddressSelect}
              />

              {/* Auto-populated zone info */}
              {selectedAddress && (project.zoneCode || project.community) && (
                <div className="p-3 bg-teal-50 border border-teal-200 rounded-lg">
                  <div className="flex items-center gap-2 text-teal-800 text-sm font-medium mb-2">
                    <MapPin className="h-4 w-4" />
                    Property Information
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {project.zoneCode && (
                      <div>
                        <span className="text-gray-500">Zone:</span>{" "}
                        <span className="font-medium text-gray-900">
                          {project.zoneCode}
                        </span>
                      </div>
                    )}
                    {project.community && (
                      <div>
                        <span className="text-gray-500">Community:</span>{" "}
                        <span className="font-medium text-gray-900">
                          {project.community}
                        </span>
                      </div>
                    )}
                    {project.parcelId && (
                      <div className="col-span-2">
                        <span className="text-gray-500">Parcel ID:</span>{" "}
                        <span className="font-mono text-xs text-gray-700">
                          {project.parcelId}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Proposed Use Dropdown */}
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  Proposed Use
                </label>
                <select
                  value={project.proposedUse}
                  onChange={(e) => updateProject("proposedUse", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white text-gray-900"
                >
                  <option value="">Select a proposed use...</option>
                  {/* Suggested uses for this zone (shown first) */}
                  {suggestedUses.length > 0 && (
                    <optgroup label={`Suggested for ${project.zoneCode}`}>
                      {proposedUseOptions
                        .filter((opt) => suggestedUses.includes(opt.value))
                        .map((opt) => (
                          <option
                            key={`suggested-${opt.value}`}
                            value={opt.value}
                          >
                            {opt.label}
                          </option>
                        ))}
                    </optgroup>
                  )}
                  {/* All options grouped by category */}
                  {[
                    "Residential",
                    "Commercial",
                    "Mixed Use",
                    "Industrial",
                    "Other",
                  ].map((category) => (
                    <optgroup key={category} label={category}>
                      {proposedUseOptions
                        .filter((opt) => opt.category === category)
                        .map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                    </optgroup>
                  ))}
                </select>
                {suggestedUses.length > 0 && (
                  <p className="text-xs text-teal-600">
                    Suggested uses for {project.zoneCode} zone shown first
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Lot Area (m²)"
                  type="number"
                  value={project.lotArea || ""}
                  onChange={(e) =>
                    updateProject("lotArea", Number(e.target.value))
                  }
                />
                <Input
                  label="Building Area (m²)"
                  type="number"
                  value={project.buildingArea || ""}
                  onChange={(e) =>
                    updateProject("buildingArea", Number(e.target.value))
                  }
                />
              </div>

              <Input
                label="Building Height (m)"
                type="number"
                value={project.height || ""}
                onChange={(e) =>
                  updateProject("height", Number(e.target.value))
                }
              />

              <div className="grid grid-cols-3 gap-4">
                <Input
                  label="Front Setback (m)"
                  type="number"
                  value={project.frontSetback || ""}
                  onChange={(e) =>
                    updateProject("frontSetback", Number(e.target.value))
                  }
                />
                <Input
                  label="Rear Setback (m)"
                  type="number"
                  value={project.rearSetback || ""}
                  onChange={(e) =>
                    updateProject("rearSetback", Number(e.target.value))
                  }
                />
                <Input
                  label="Side Setback (m)"
                  type="number"
                  value={project.sideSetback || ""}
                  onChange={(e) =>
                    updateProject("sideSetback", Number(e.target.value))
                  }
                />
              </div>

              <Input
                label="Parking Spaces"
                type="number"
                value={project.parkingSpaces || ""}
                onChange={(e) =>
                  updateProject("parkingSpaces", Number(e.target.value))
                }
              />

              <Button
                onClick={handleAnalyze}
                disabled={
                  assessmentMutation.isPending ||
                  !project.address ||
                  !project.proposedUse
                }
                className="w-full bg-teal-600 hover:bg-teal-700"
              >
                {assessmentMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <TrendingUp className="h-4 w-4 mr-2" />
                    Analyze Risk
                  </>
                )}
              </Button>

              {assessmentMutation.isError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
                  <AlertCircle className="h-4 w-4" />
                  Failed to analyze risk. Please try again.
                </div>
              )}
            </div>
          </Card>

          {/* Results */}
          <div className="space-y-4">
            {assessment ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4"
              >
                {/* Overall Risk */}
                <RiskIndicator
                  level={assessment.overall_risk}
                  probability={assessment.approval_probability}
                  size="lg"
                />

                {/* Risk Factors */}
                <Card variant="outlined">
                  <h3 className="font-semibold text-gray-900 mb-4">
                    Risk Factors
                  </h3>
                  <div className="space-y-3">
                    {assessment.factors.map((factor, index) => (
                      <div
                        key={index}
                        className="p-3 rounded-lg bg-gray-50 border border-gray-100"
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-gray-900">
                            {factor.category}
                          </span>
                          <Badge
                            variant={
                              factor.impact === "HIGH"
                                ? "success"
                                : factor.impact === "MEDIUM"
                                  ? "warning"
                                  : "danger"
                            }
                          >
                            {factor.impact}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-600">
                          {factor.description}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {factor.details}
                        </p>
                      </div>
                    ))}
                  </div>
                </Card>

                {/* Similar Cases */}
                {similarCasesQuery.data &&
                  similarCasesQuery.data.length > 0 && (
                    <Card variant="outlined">
                      <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <Search className="h-4 w-4 text-blue-500" />
                        Similar SDAB Cases
                      </h3>
                      <div className="space-y-3">
                        {similarCasesQuery.data
                          .slice(0, 3)
                          .map((sCase, index) => (
                            <div
                              key={index}
                              className="p-3 rounded-lg bg-blue-50 border border-blue-100"
                            >
                              <div className="flex items-center justify-between mb-1">
                                <span className="font-medium text-gray-900">
                                  {sCase.case_number || `Case ${index + 1}`}
                                </span>
                                <Badge
                                  variant={
                                    sCase.decision === "APPROVED" ||
                                    sCase.decision ===
                                      "APPROVED_WITH_CONDITIONS"
                                      ? "success"
                                      : "danger"
                                  }
                                >
                                  {sCase.decision.replace(/_/g, " ")}
                                </Badge>
                              </div>
                              {sCase.property_type && (
                                <p className="text-sm text-gray-600">
                                  {sCase.property_type}
                                </p>
                              )}
                              {sCase.variance_type && (
                                <p className="text-xs text-gray-500 mt-1">
                                  Variance: {sCase.variance_type}
                                </p>
                              )}
                            </div>
                          ))}
                      </div>
                    </Card>
                  )}

                {/* Recommendations */}
                <Card variant="outlined">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <FileText className="h-4 w-4 text-teal-500" />
                    Recommendations to Improve Approval
                  </h3>
                  <ul className="space-y-2">
                    {assessment.recommendations.map((rec, index) => (
                      <li
                        key={index}
                        className="flex items-start gap-2 text-gray-600"
                      >
                        <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                        {rec}
                      </li>
                    ))}
                  </ul>
                </Card>
              </motion.div>
            ) : (
              <Card variant="outlined" className="text-center py-12">
                <Scale className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">
                  Enter your project details to get a risk assessment
                </p>
                <p className="text-sm text-gray-400 mt-1">
                  Our expert system analyzes zoning requirements and SDAB
                  precedents
                </p>
              </Card>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
